import React, { useState, useEffect } from "react";
import {
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Paper,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { updateCommitment, replaceCommitment, deleteCommitment } from "../../api/commitments";
import PlanIntelligenceSuggestion from "./PlanIntelligenceSuggestion";
import PendingActionNotice from "../common/PendingActionNotice";

const ACTIVE_DAY_OPTIONS = [
  { label: "M", value: 0 },
  { label: "T", value: 1 },
  { label: "W", value: 2 },
  { label: "T", value: 3 },
  { label: "F", value: 4 },
  { label: "S", value: 5 },
  { label: "S", value: 6 },
];

const ALL_ACTIVE_DAYS = ACTIVE_DAY_OPTIONS.map((day) => day.value);

function habitTitleTokens(value) {
  return String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .split(" ")
    .map((token) => token.trim())
    .filter((token) => token.length > 2);
}

function habitTitleSimilarity(left, right) {
  const leftTokens = new Set(habitTitleTokens(left));
  const rightTokens = new Set(habitTitleTokens(right));
  if (!leftTokens.size || !rightTokens.size) return 1;
  const shared = [...leftTokens].filter((token) => rightTokens.has(token)).length;
  return shared / Math.max(leftTokens.size, rightTokens.size);
}

function habitTitleChangeLooksLikeReplacement(commitment, nextTitle) {
  const current = String(commitment?.title || "").trim();
  const next = String(nextTitle || "").trim();
  if (!current || !next || current.toLowerCase() === next.toLowerCase()) return false;
  return habitTitleSimilarity(current, next) < 0.4;
}

function CommitmentEditDialog({ commitment, onClose, onSaved, intelContext = null }) {
  const [title, setTitle] = useState(commitment.title);
  const [frequency, setFrequency] = useState(commitment.frequency);
  const [minimumVersionTitle, setMinimumVersionTitle] = useState(
    commitment.minimum_version_title || ""
  );
  const [preferredTime, setPreferredTime] = useState(
    commitment.preferred_time ? String(commitment.preferred_time).slice(0, 5) : ""
  );
  const [doneCriteria, setDoneCriteria] = useState(commitment.done_criteria || "");
  const [cueAnchor, setCueAnchor] = useState(commitment.cue_anchor || "");
  const [cueContext, setCueContext] = useState(commitment.cue_context || "");
  const [cueLocationLabel, setCueLocationLabel] = useState(commitment.cue_location_label || "");
  const [activeDays, setActiveDays] = useState(
    commitment.active_days?.length ? commitment.active_days : ALL_ACTIVE_DAYS
  );
  const [loading, setLoading] = useState(false);
  const [loadingMode, setLoadingMode] = useState("");
  const [error, setError] = useState("");
  const [replacementChoiceOpen, setReplacementChoiceOpen] = useState(false);
  const replacementHintVisible = habitTitleChangeLooksLikeReplacement(commitment, title);

  // 🔑 keep state in sync when switching commitments
  useEffect(() => {
    setTitle(commitment.title);
    setFrequency(intelContext?.suggestedFrequency || commitment.frequency);
    setMinimumVersionTitle(commitment.minimum_version_title || "");
    setPreferredTime(commitment.preferred_time ? String(commitment.preferred_time).slice(0, 5) : "");
    setDoneCriteria(commitment.done_criteria || "");
    setCueAnchor(commitment.cue_anchor || "");
    setCueContext(commitment.cue_context || "");
    setCueLocationLabel(commitment.cue_location_label || "");
    setActiveDays(
      commitment.active_days?.length ? commitment.active_days : ALL_ACTIVE_DAYS
    );
    setLoading(false);
    setLoadingMode("");
    setError("");
    setReplacementChoiceOpen(false);
  }, [commitment, intelContext?.suggestedFrequency]);

  function buildPayload(options = {}) {
    return {
      title: title.trim(),
      frequency,
      active_days: frequency === "daily" ? activeDays : ALL_ACTIVE_DAYS,
      minimum_version_title: minimumVersionTitle.trim(),
      preferred_time: preferredTime || null,
      done_criteria: doneCriteria.trim(),
      cue_anchor: cueAnchor.trim(),
      cue_context: cueContext.trim(),
      cue_location_label: cueLocationLabel.trim(),
      ...(options.allowHistoryMerge ? { allow_history_merge: true } : {}),
    };
  }

  async function handleSave(options = {}) {
    if (replacementHintVisible && !options.replace && !options.allowHistoryMerge) {
      setReplacementChoiceOpen(true);
      return;
    }
    setLoading(true);
    setLoadingMode(options.replace ? "replace" : "save");
    setError("");
    setReplacementChoiceOpen(false);
    try {
      const payload = buildPayload({ allowHistoryMerge: options.allowHistoryMerge });
      const result = options.replace
        ? await replaceCommitment(commitment.id, payload)
        : await updateCommitment(commitment.id, payload);
      setLoading(false);
      setLoadingMode("");
      onSaved?.({
        updatedCommitment: result?.replacement || result,
        replacedCommitment: result?.replaced || null,
        commitmentId: commitment.id,
        replaced: Boolean(options.replace),
      });
      onClose();
    } catch (err) {
      if (
        err?.errors?.code === "habit_replacement_recommended" &&
        !options.replace &&
        !options.allowHistoryMerge
      ) {
        setReplacementChoiceOpen(true);
        setLoading(false);
        setLoadingMode("");
        return;
      }
      setError(err?.message || "Unable to save this habit.");
      setLoading(false);
      setLoadingMode("");
    }
  }

  async function handleDelete() {
    if (!window.confirm("Delete this habit?")) return;

    setLoading(true);
    setLoadingMode("delete");
    setError("");
    onSaved?.({ deleted: true, commitmentId: commitment.id, optimistic: true });
    onClose();
    try {
      await deleteCommitment(commitment.id);
      onSaved?.({ deleted: true, commitmentId: commitment.id });
    } catch (err) {
      const message = err?.message || "";
      if (!message.includes("No Commitment matches")) {
        console.error("Failed to delete habit", err);
      }
    } finally {
      setLoading(false);
      setLoadingMode("");
    }
  }

  return (
    <Dialog open onClose={loading ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit habit</DialogTitle>

      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {loading ? (
            <PendingActionNotice
              compact
              title={loadingMode === "replace" ? "Replacing habit" : loadingMode === "delete" ? "Deleting habit" : "Saving habit"}
              body={
                loadingMode === "replace"
                  ? "Archiving the old habit and setting up the new one."
                  : loadingMode === "delete"
                    ? "Removing this habit from your plan."
                    : "Updating this habit and refreshing your plan."
              }
            />
          ) : null}

          {error ? <Alert severity="error">{error}</Alert> : null}

          {replacementHintVisible && !replacementChoiceOpen ? (
            <Alert
              severity="info"
              action={
                <Stack direction="row" spacing={0.75}>
                  <Button size="small" onClick={() => handleSave({ allowHistoryMerge: true })} disabled={loading}>
                    Just rename
                  </Button>
                  <Button size="small" variant="contained" onClick={() => handleSave({ replace: true })} disabled={loading}>
                    Replace habit
                  </Button>
                </Stack>
              }
            >
              This looks like a different behavior. Replace keeps old completions, proof, and insights attached to the old habit.
            </Alert>
          ) : null}

          {replacementChoiceOpen ? (
            <Alert
              severity="warning"
              action={
                <Stack direction="row" spacing={0.75}>
                  <Button size="small" onClick={() => handleSave({ allowHistoryMerge: true })} disabled={loading}>
                    Just rename
                  </Button>
                  <Button size="small" variant="contained" onClick={() => handleSave({ replace: true })} disabled={loading}>
                    Replace habit
                  </Button>
                </Stack>
              }
            >
              TrustBud found existing history for this habit. Replace it to keep analytics clean, or just rename if only the wording changed.
            </Alert>
          ) : null}

          {intelContext && (
            <Paper
              elevation={0}
              sx={{
                border: "1px solid #bfdbfe",
                bgcolor: "#eff6ff",
                borderRadius: 2,
                p: 1.5,
              }}
            >
              <Typography sx={{ fontWeight: 850, color: "#1e3a8a" }}>
                {intelContext.title}
              </Typography>
              <Typography variant="body2" sx={{ color: "#334155", mt: 0.4 }}>
                {intelContext.body}
              </Typography>
            </Paper>
          )}

          <TextField
            label="Habit"
            value={title}
            onChange={e => setTitle(e.target.value)}
            fullWidth
            autoFocus={!intelContext || intelContext.primaryField === "habit"}
          />

          <TextField
            select
            label="Frequency"
            value={frequency}
            onChange={e => setFrequency(e.target.value)}
            fullWidth
            autoFocus={intelContext?.primaryField === "cadence"}
            SelectProps={{ native: true }}
          >
            <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
          </TextField>
          <TextField
            label="Keep-alive step"
            placeholder="Walk for 3 minutes"
            value={minimumVersionTitle}
            onChange={(e) => setMinimumVersionTitle(e.target.value)}
            helperText="Optional. This keeps the habit alive on low-energy days."
            fullWidth
            autoFocus={intelContext?.primaryField === "small_version"}
          />
          <TextField
            label="What counts as done?"
            placeholder="e.g. Walk outside for at least 20 minutes"
            value={doneCriteria}
            onChange={(e) => setDoneCriteria(e.target.value)}
            helperText="Make the finish line clear so you and your TrustBuds know what counted."
            fullWidth
            autoFocus={intelContext?.primaryField === "done_criteria"}
          />
          <TextField
            label="Planned time"
            type="time"
            value={preferredTime}
            onChange={(e) => setPreferredTime(e.target.value)}
            helperText="TrustBud will check in if this time passes and it is not done."
            InputLabelProps={{ shrink: true }}
            fullWidth
            autoFocus={intelContext?.primaryField === "planned_time"}
          />
          <TextField
            label="Routine cue"
            placeholder="After breakfast"
            value={cueAnchor}
            onChange={(e) => setCueAnchor(e.target.value)}
            helperText="Optional. Use an existing routine as the trigger."
            fullWidth
            autoFocus={intelContext?.primaryField === "cue_anchor"}
          />
          <TextField
            label="Place or situation cue"
            placeholder="At my desk, after school drop-off"
            value={cueContext}
            onChange={(e) => setCueContext(e.target.value)}
            helperText="Optional. Name the context where this habit usually starts."
            fullWidth
            autoFocus={intelContext?.primaryField === "cue_context"}
          />
          <TextField
            label="Saved place cue"
            placeholder="Gym, office, kitchen table"
            value={cueLocationLabel}
            onChange={(e) => setCueLocationLabel(e.target.value)}
            helperText="Optional. Web saves the place label; iPhone handles device reminders."
            fullWidth
            autoFocus={intelContext?.primaryField === "cue_location"}
          />
          <PlanIntelligenceSuggestion
            itemType="commitment"
            payload={{ title, frequency }}
            applyLabel="Use suggested habit"
            onApply={(suggestion) => {
              if (suggestion.suggested_title) setTitle(suggestion.suggested_title);
            }}
          />

          {frequency === "daily" && (
            <Stack spacing={1}>
              <Typography variant="subtitle2">
                Which days does this habit apply?
              </Typography>
              <ToggleButtonGroup
                value={activeDays}
                onChange={(_, next) => {
                  if (!next.length) return;
                  setActiveDays(next);
                }}
                size="small"
                sx={{
                  "& .MuiToggleButton-root": {
                    borderColor: "divider",
                    color: "text.secondary",
                  },
                  "& .MuiToggleButton-root.Mui-selected": {
                    bgcolor: "text.primary",
                    color: "common.white",
                    borderColor: "text.primary",
                    "&:hover": {
                      bgcolor: "text.primary",
                    },
                  },
                }}
              >
                {ACTIVE_DAY_OPTIONS.map((day) => (
                  <ToggleButton key={day.value} value={day.value}>
                    {day.label}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
              <Typography variant="caption" color="text.secondary">
                Inactive days won't count against your streak.
              </Typography>
            </Stack>
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ justifyContent: "space-between", px: 3 }}>
        <Button color="error" onClick={handleDelete} disabled={loading}>
          Delete
        </Button>

        <Stack direction="row" spacing={1}>
          <Button onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => handleSave()}
            disabled={loading || !title.trim()}
          >
            {loading ? (loadingMode === "replace" ? "Replacing..." : "Saving...") : intelContext?.saveLabel || "Save"}
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
}

export default CommitmentEditDialog;
